'use strict';

class GeolocationModel {
    constructor() {
        this.latitude = '';
        this.longitude = '';
        this.locationText = 'Use My location';
        this.error = false;
        this.errorMessage = '';
        this.timestamp = '';
    }

    /**
     * Sets the error information based on the error code.
     * @param {Object} error - The error object from the geolocation API.
     */
    setError(error) {
        this.error = true;
        switch (error.code) {
            case error.PERMISSION_DENIED:
                this.locationText = 'User denied geolocation.<br/>Change settings in your browser.';
                break;
            case error.POSITION_UNAVAILABLE:
                this.errorMessage = 'Geolocation information is unavailable. Please try again.';
                break;
            case error.TIMEOUT:
                this.errorMessage = 'Location request has timed out. Please try again.';
                break;
            case error.UNKNOWN_ERROR:
                this.errorMessage = 'Unknown error occurred. Please try again.';
                break;
        }
    }

    setNotSupported() {
        this.error = true;
        this.locationText = 'Geolocation is not supported by this browser.';
    }
}

/**
 * Retrieves geolocation data from window.sessionStorage if available.
 * @returns {GeolocationModel|null} A GeolocationModel instance if data exists, otherwise null.
 */
function getGeolocationFromSessionStorage() {
    const storedData = window.sessionStorage.getItem('geolocationData');
    if (storedData) {
        const parsedData = JSON.parse(storedData);
        const geolocationModel = new GeolocationModel();
        geolocationModel.latitude = parsedData.latitude;
        geolocationModel.longitude = parsedData.longitude;
        geolocationModel.locationText = parsedData.locationText;
        geolocationModel.timestamp = parsedData.timestamp;
        return geolocationModel;
    }
    return null;
}

/**
 * Saves the geolocation data to window.sessionStorage.
 * @param {GeolocationModel} geolocationModel - The geolocation model containing the data to save.
 */
function saveGeolocationToSessionStorage(geolocationModel) {
    window.sessionStorage.setItem('geolocationData', JSON.stringify(geolocationModel));
}

/**
 * Removes the geolocation data from window.sessionStorage.
 */
function removeGeolocationToSessionStorage() {
    window.sessionStorage.removeItem('geolocationData');
}

/**
 * Checks if the user has granted permission for geolocation.
 * @returns {Promise<boolean>} A promise that resolves to `true` if geolocation is allowed, otherwise `false`.
 */
function isLocationAllowed() {
    return new Promise((resolve) => {
        navigator.permissions.query({ name: "geolocation" }).then((result) => {
            if (result.state !== "granted") {
                resolve(false);
                return;
            }
            resolve(true);
        });
    });
}

/**
 * Retrieves the current geolocation coordinates of the user's device.
 * @returns {Promise<GeolocationModel>}
 * A promise that resolves with an instance of `GeolocationModel` containing the geolocation data or error information.
 */
function getGeolocation() {
    return new Promise((resolve) => {
        function proceed() {
            const geolocationModel = new GeolocationModel();
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        geolocationModel.latitude = position.coords.latitude;
                        geolocationModel.longitude = position.coords.longitude;
                        geolocationModel.timestamp = Date.now();
                        saveGeolocationToSessionStorage(geolocationModel);
                        resolve(geolocationModel);
                    },
                    (error) => {
                        geolocationModel.setError(error);
                        resolve(geolocationModel);
                    }
                );
            } else {
                geolocationModel.setNotSupported();
                resolve(geolocationModel);
            }
        }

        const geolocationModelFromStorage = getGeolocationFromSessionStorage();
        if (geolocationModelFromStorage) {
            const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
            const currentTime = Date.now();
            const isRecent = currentTime - geolocationModelFromStorage.timestamp < oneDayInMilliseconds;
            isLocationAllowed().then((isAllowed) => {
                if (isRecent && isAllowed) {
                    resolve(geolocationModelFromStorage);
                } else {
                    removeGeolocationToSessionStorage();
                    proceed();
                }
            });
        } else {
            proceed();
        }
    });
}

module.exports = {
    GeolocationModel: GeolocationModel,
    getGeolocation: getGeolocation
};
